import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Trophy, ArrowRight, Target, CheckCircle2, Zap, TrendingUp, Share2 } from 'lucide-react';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { logger } from '../utils/logger';
import { analytics } from '../utils/analytics';
import { API_BASE as API_URL } from '../utils/apiBase';

const getScoreColor = (score) => {
  if (score >= 85) return '#10b981';
  if (score >= 70) return '#22c55e';
  if (score >= 50) return '#eab308';
  return '#e11d48';
};

const getScoreTier = (score) => {
  if (score >= 85) return { label: 'Elite', emoji: '🏆' };
  if (score >= 70) return { label: 'Strong', emoji: '💪' };
  if (score >= 50) return { label: 'Promising', emoji: '📈' };
  return { label: 'Needs Work', emoji: '🎯' };
};

const ScoreShare = () => {
  const { shareId } = useParams();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/share/score/${shareId}`);
        setShare(res.data);
      } catch (err) {
        logger.error('Failed to load share:', err.message);
        setError('This score link has expired or no longer exists.');
      } finally {
        setLoading(false);
      }
    })();
  }, [shareId]);

  // Compute share metadata once `share` resolves — these drive the OG tags
  // crawlers and social previewers fetch when the link is pasted on
  // LinkedIn/X/Slack. Empty values when loading; the hook is no-op then.
  const shareTier = share ? getScoreTier(share.score) : null;
  const shareTitle = share
    ? `${share.name} scored ${share.score}/100 on Ascend's ATS Checker`
    : undefined;
  const shareDesc = share && shareTier
    ? `${share.name} just got a ${shareTier.label} ATS score (${share.score}/100) for a ${share.role} resume on Ascend. Think you can beat it? Check your own resume free.`
    : undefined;
  useDocumentMeta({
    title: shareTitle,
    description: shareDesc,
    path: shareId ? `/score/${shareId}` : undefined,
    imageAlt: shareTitle ? `${shareTitle} — viral score-card preview` : undefined,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1115] flex items-center justify-center">
        <div className="text-white/60">Loading score card…</div>
      </div>
    );
  }

  if (error || !share) {
    return (
      <div className="min-h-screen bg-[#0f1115] flex flex-col items-center justify-center text-center px-6">
        <Trophy size={48} className="text-[#eab308] mb-4" />
        <h1 className="text-white text-2xl font-bold mb-2">Score Card Not Found</h1>
        <p className="text-white/60 mb-6">{error || 'This link may have expired.'}</p>
        <Link to="/free-ats-check" className="px-5 py-3 bg-white text-[#0f1115] font-bold hover:bg-[#f4f5f7] transition-colors">
          Check Your Own Resume Score Free →
        </Link>
      </div>
    );
  }

  const { name, score, role, top_keywords: topKeywords = [], created_at: createdAt } = share;
  const tier = shareTier;
  const color = getScoreColor(score);
  const circumference = 2 * Math.PI * 90;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f1115] via-[#111827] to-[#002fa7]" data-testid="score-share-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        {/* Brand bar */}
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="text-white/90 font-satoshi text-lg font-black tracking-tight">ASCEND</Link>
          <Link to="/free-ats-check" className="text-sm text-white/70 hover:text-white transition-colors">Free ATS Check</Link>
        </div>

        {/* Score card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white p-8 sm:p-12 shadow-2xl"
          data-testid="score-card"
        >
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#002fa7] mb-4">
            <Trophy size={14} /> ATS Score Card · {tier.emoji} {tier.label}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-8 items-center mb-8">
            {/* Score gauge */}
            <div className="relative flex items-center justify-center">
              <svg width="200" height="200" viewBox="0 0 200 200">
                <circle cx="100" cy="100" r="90" fill="none" stroke="#f4f5f7" strokeWidth="12" />
                <motion.circle
                  cx="100" cy="100" r="90" fill="none" stroke={color} strokeWidth="12"
                  strokeLinecap="round" strokeDasharray={circumference}
                  initial={{ strokeDashoffset: circumference }}
                  animate={{ strokeDashoffset: offset }}
                  transition={{ duration: 1.4, ease: 'easeOut' }}
                  transform="rotate(-90 100 100)"
                />
                <text x="100" y="100" textAnchor="middle" dominantBaseline="central" fontSize="52" fontWeight="900" fill="#0f1115" style={{ fontFamily: 'Satoshi, sans-serif' }}>
                  {score}
                </text>
                <text x="100" y="140" textAnchor="middle" fontSize="14" fill="#6b7280" fontWeight="600">OUT OF 100</text>
              </svg>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#6b7280] mb-2">Candidate</p>
              <h1 className="font-satoshi text-3xl sm:text-4xl font-black text-[#0f1115] mb-3">{name}</h1>
              <p className="text-sm font-bold uppercase tracking-widest text-[#6b7280] mb-1">Target Role</p>
              <p className="text-lg text-[#0f1115] font-semibold mb-4">{role}</p>
              {createdAt && (
                <p className="text-xs text-[#9ca3af]">Scored {new Date(createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
              )}
            </div>
          </div>

          {/* Top Keywords Matched */}
          {topKeywords.length > 0 && (
            <div className="mb-8">
              <p className="text-xs font-bold uppercase tracking-widest text-[#6b7280] mb-3 flex items-center gap-2">
                <Target size={14} /> Top Keywords Matched
              </p>
              <div className="flex flex-wrap gap-2" data-testid="share-keywords">
                {topKeywords.slice(0, 12).map(k => (
                  <span key={k} className="px-3 py-1 bg-[#10b981]/10 text-[#065f46] text-sm font-medium">
                    <CheckCircle2 size={12} className="inline mr-1" />{k}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Beat My Score CTA */}
          <div className="bg-gradient-to-br from-[#002fa7] to-[#001d6a] text-white p-6 sm:p-8 text-center" data-testid="beat-challenge">
            <TrendingUp size={28} className="mx-auto mb-3 opacity-90" />
            <h2 className="font-satoshi text-xl sm:text-2xl font-black mb-2">Think you can beat {score}?</h2>
            <p className="text-white/80 text-sm mb-5">Get your own ATS score in 30 seconds. Free. No signup needed.</p>
            <Link
              to={`/free-ats-check?challenger=${encodeURIComponent(name)}&beat=${score}`}
              onClick={() => analytics.clickCTA('beat_score_challenge', 'score_share_page')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#002fa7] font-bold hover:bg-[#f4f5f7] transition-colors"
              data-testid="beat-cta"
            >
              Beat {name}'s Score <ArrowRight size={16} />
            </Link>
          </div>

          {/* Small share hint */}
          <p className="text-center text-xs text-[#9ca3af] mt-6 flex items-center justify-center gap-2">
            <Share2 size={12} /> Powered by Ascend — the AI career assistant
          </p>
        </motion.div>

        {/* Secondary CTA */}
        <div className="text-center mt-10">
          <Link to="/" className="text-white/70 hover:text-white transition-colors text-sm inline-flex items-center gap-2" data-testid="learn-more">
            Learn more about Ascend <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ScoreShare;
